import { useState } from 'react';
import { useSnapshot } from 'valtio';

import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Image from '@cartwhl/ui/atoms/Image';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { useLocalStorage } from 'hooks/useLocalStorage';
import { animationGenerationStore } from 'stores';

const key = 'skipWarnBadMotionPredictedModal';

export default function WarnBadMotionPredictedModal({
  close,
  payload,
}: {
  close: () => void;
  payload?: { submit?: () => void };
}) {
  const { prompt } = useSnapshot(animationGenerationStore.state);
  const [checked, setChecked] = useState(false);
  const [skipModal, setSkipModal] = useLocalStorage(key, false);

  // if skip modal, we need to auto submit the generation for them.
  if (skipModal) {
    if (payload?.submit) {
      payload.submit();
    }
    return null;
  }

  const title = (
    <div>
      <Text as="p" variant="studio/title/xl">
        That's a great prompt!
      </Text>
      <Text as="p" variant="studio/body/medium/sm">
        (Maybe TOO great...)
      </Text>
    </div>
  );

  return (
    <Modal
      title={title}
      open={true}
      footer={
        <Checkbox
          id="warn-bad-motion-predicted-modal-checkbox"
          setChecked={setChecked}
          checked={checked}
          label="Don't show again"
          variant="checkbox"
          color="settings"
        />
      }
    >
      <div className="space-y-4">
        <Image
          src={
            'https://resources.getcartwheel.com/share/images/modals/awesome_prompt.png'
          }
          alt={`Cartwheel characters celebrating.`}
          className="bg-gray-15 mb-4 block aspect-452/254 w-full rounded-2xl"
        />
        <Text as="p" variant="studio/body/sm">
          Text-to-motion is still in beta. Some prompts, depending on their
          length and phrasing, may produce less then ideal results.
        </Text>
        <Text as="p" variant="studio/body/sm">
          We noticed that your specific prompt,{' '}
          <b>
            "{prompt.length > 40 ? prompt.substring(0, 40) + '...' : prompt}"
          </b>{' '}
          has a high chance of producing something not so great. If you want
          some tips on how to craft prompts that will work well, check out our{' '}
          <a
            href="https://docs.getcartwheel.com/docs/getting-started/prompt-introduction"
            target="_blank"
            className="text-blue underline"
          >
            prompting documentation
          </a>
          .
        </Text>
        <Text as="p" variant="studio/body/sm">
          If you're still interested to see what the model will generate, then
          go ahead and select "Continue!"
        </Text>
      </div>

      <div className="mt-6 flex justify-between">
        <Button
          variant="secondary-destructive"
          onClick={() => {
            close();
          }}
        >
          Cancel generation
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (checked) {
              setSkipModal(true);
            }
            if (payload?.submit) {
              payload.submit();
            }
            close();
          }}
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
}
